import compact from 'lodash/compact'
import groupBy from 'lodash/groupBy'
import type { $Values } from 'utility-types'

import createComputed from 'modules/core/hooks/createComputed'
import users from 'modules/core/store/collections/users'

import type AnalyticResult from 'modules/reviews/store/models/AnalyticResult'
import type { AnalyticQuestionGroup } from 'modules/reviews/store/models/AnalyticResult'
import type { Answers } from 'modules/surveys/store/models/AnalyticResult'
import { AggregateOperations } from 'modules/surveys/store/models/AnalyticResult'
import { GroupedBy } from 'modules/surveys/store/models/AnalyticResult'

import type ScoreForm from './ScoreForm'

export const useKeys = createComputed((analyticResult: AnalyticResult, scoreForm: ScoreForm) => {
  if (scoreForm.groupedBy === GroupedBy.Questions) {
    return Array.from(analyticResult.numericQuestions).map((question) => question.label)
  }

  return Object.keys(analyticResult.numericCategories).map((categoryId: string) => {
    return analyticResult.numericCategories[Number(categoryId)]?.name ?? ''
  })
})
interface Data {
  data: Array<Record<string, any>>
  maxValue: number
  minValue: number
}

// TODO: check types after TS migration
interface Result {
  targetId: string
  targetName: string | undefined
  [label: string]: string | number | undefined
}

const computeQuestionsData = (
  analyticResult: AnalyticResult,
  questionGroups?: AnalyticQuestionGroup[]
): Data => {
  let maxValue = 0
  let minValue = 0

  const groupedQuestionGroups = groupBy(questionGroups, 'target_id')

  const data = Object.entries(groupedQuestionGroups).map(([targetId, targetGroup]) => {
    const targetName = users.get(Number(targetId))?.fullName()

    const result: Result = {
      targetId,
      targetName,
    }

    const answersArray: Answers[] = targetGroup.map((quertionGroup) => quertionGroup.answers)

    // @nivo/heatmap does not allow us to render questions with the
    // same label. We are giving each question a position in order
    // to differentiate them.
    let question_position = 1
    analyticResult.numericQuestions.forEach((question) => {
      const valuesArray = compact(answersArray.map((answer) => answer[question.id]))
      const question_label = question_position.toString() + '. ' + question.label
      question_position += 1

      // If we don't have an answer, we return NaN and we set it to transparent
      // color using nanColor prop in the HeatMap component
      if (!valuesArray.length) {
        result[question_label] = NaN

        return
      }

      const sumValue = valuesArray.reduce<number>((memo, answer) => memo + Number(answer), 0)

      const value = sumValue / valuesArray.length
      maxValue = Math.max(maxValue, value)
      minValue = Math.min(minValue, value)
      result[question_label] = formatValue(value)
    })

    return result
  })

  return {
    data,
    maxValue,
    minValue,
  }
}

const computeSectionsData = (
  analyticResult: AnalyticResult,
  aggregateOperation: $Values<typeof AggregateOperations>,
  questionGroups?: AnalyticQuestionGroup[]
): Data => {
  let maxValue = 0
  let minValue = 0

  const groupedQuestionGroups = groupBy(questionGroups, 'target_id')

  const data = Object.entries(groupedQuestionGroups).map(([targetId, targetGroup]) => {
    const targetName = users.get(Number(targetId))?.fullName()

    const result: Result = {
      targetId,
      targetName,
    }

    const answersArray: Answers[] = targetGroup.map((group) => group.answers)

    // @nivo/heatmap does not allow us to render sections with the
    // same label. We are giving each section a position in order
    // to differentiate them.
    let sectionPosition = 1

    Object.keys(analyticResult.numericCategories).forEach((categoryId: string) => {
      const category = analyticResult.numericCategories[Number(categoryId)]
      const { questionsIds, name } = category ?? { questionsIds: [], name: '' }

      const valuesArray = compact(
        answersArray.flatMap((answers) =>
          Array.from(questionsIds).map((questionId) => answers[questionId])
        )
      )

      const sectionName = sectionPosition.toString() + '. ' + name
      sectionPosition += 1

      // If we don't have data, we return NaN and we set it to transparent
      // color using nanColor prop in the HeatMap component
      if (valuesArray.length === 0) {
        result[sectionName] = NaN

        return
      }

      let value = valuesArray.reduce<number>((memo, answer) => memo + Number(answer), 0)

      if (aggregateOperation === AggregateOperations.Avg) {
        value /= valuesArray.length || 0
      }

      maxValue = Math.max(maxValue, value)
      minValue = Math.min(minValue, value)
      result[sectionName] = formatValue(value)
    })

    return result
  }, {})

  return {
    data,
    maxValue,
    minValue,
  }
}

export const useData = createComputed(
  (
    analyticResult: AnalyticResult,
    scoreForm: ScoreForm,
    questionGroups?: AnalyticQuestionGroup[]
  ) => {
    if (scoreForm.groupedBy === GroupedBy.Questions) {
      return computeQuestionsData(analyticResult, questionGroups)
    }

    return computeSectionsData(analyticResult, scoreForm.aggregateOperation, questionGroups)
  }
)

const formatValue = (value: number): string => {
  return value.toFixed(2).replace(/[.,]00$/, '')
}
